/**
 * KYC2 attempts in Customer 360 «احراز هویت» (Phase 4). Each attempt is immutable history: status,
 * rules version accepted, correction targets, decision reason. Evidence (the selfie with ID card and
 * handwritten note) is private: it is fetched only on an explicit click, by POST, as bytes (never a
 * URL), after a recent admin step-up; every view/download is audited (OD-41 D). `VIEW` returns the
 * metadata-stripped rendition, `DOWNLOAD` the original. The object URL lives only in this component
 * and is revoked on close/unmount; nothing is cached or stored in the browser.
 */
import { Badge, Button, StateView, TextField } from '@hopex/ui';
import { useEffect, useState } from 'react';
import { api, ApiFailure, dt, errorMessage, type Kyc2Submission } from '../api.ts';
import { TechCode } from '../lib/ui.tsx';

const STATUS: Record<
  string,
  { label: string; tone: 'positive' | 'negative' | 'accent' | 'neutral' }
> = {
  SUBMITTED: { label: 'در انتظار بررسی', tone: 'accent' },
  APPROVED: { label: 'تأییدشده', tone: 'positive' },
  NEEDS_CORRECTION: { label: 'نیازمند اصلاح', tone: 'accent' },
  REJECTED: { label: 'ردشده', tone: 'negative' },
  SUPERSEDED: { label: 'جایگزین‌شده', tone: 'neutral' },
};
const TARGET: Record<string, string> = {
  FATHER_NAME: 'نام پدر',
  SELFIE: 'تصویر سلفی / ویدیو',
  RULES: 'پذیرش قوانین',
};
const EXT: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'video/webm': 'webm',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
};
/** Jibit biometric outcome (OD-58) → Persian label for the reviewer; the raw code stays technical. */
const BIO: Record<string, { label: string; tone: 'positive' | 'negative' | 'accent' | 'neutral' }> =
  {
    PASS: { label: 'هر سه بررسی قبول (تأیید خودکار)', tone: 'positive' },
    FACE_MISMATCH: { label: 'چهره با ثبت احوال مطابقت ندارد', tone: 'negative' },
    NOT_LIVE: { label: 'زنده‌بودن تأیید نشد', tone: 'negative' },
    SPEECH_MISMATCH: { label: 'جمله درست خوانده نشد', tone: 'accent' },
    IDENTITY_INVALID: { label: 'هویت در ثبت احوال یافت نشد', tone: 'negative' },
    CARD_EXPIRED: { label: 'کارت ملی منقضی است', tone: 'accent' },
    UNAVAILABLE: { label: 'بررسی خودکار انجام نشد (اختلال سرویس / ثبت احوال)', tone: 'neutral' },
    ERROR: { label: 'خطای بررسی خودکار', tone: 'neutral' },
  };

function BiometricResult({
  b,
  submissionId,
  open,
}: {
  b: NonNullable<Kyc2Submission['biometric']>;
  submissionId: string;
  open: boolean;
}) {
  const [again, setAgain] = useState<string>('idle');
  const requeue = async () => {
    setAgain('busy');
    try {
      await api.post(`/kyc2/submissions/${submissionId}/requeue`, {});
      setAgain('done');
    } catch (e) {
      setAgain(errorMessage(e));
    }
  };
  // Owner 2026-10-17: say why no automatic check ran, in words (not a code).
  const o =
    b.category === 'DAILY_CAP'
      ? { label: 'سقف ۳ بررسی خودکار در ۲۴ ساعت برای این کاربر پر بود', tone: 'accent' as const }
      : /کیف پول/.test(b.category ?? '')
        ? {
            label: 'کیف پول جیبیت خالی بود؛ پس از شارژ «بررسی هوشمند دوباره»',
            tone: 'accent' as const,
          }
        : b.outcome
          ? BIO[b.outcome]
          : null;
  const running = b.state === 'QUEUED' || b.state === 'RUNNING';
  const pct = (v: string | null) => (v === null ? '—' : `${Number(v).toLocaleString('fa-IR')}٪`);
  return (
    <div
      className="flex flex-col gap-1.5 rounded-md border border-line bg-surface-1 p-2.5"
      data-kyc2-biometric
    >
      <span className="text-[12.5px] font-semibold">
        بررسی بیومتریک جیبیت:{' '}
        {running ? (
          <Badge tone="accent">در حال بررسی</Badge>
        ) : b.state === 'NONE' ? (
          <Badge tone="neutral">غیرفعال — فقط بررسی کارشناس</Badge>
        ) : o ? (
          <Badge tone={o.tone}>{o.label}</Badge>
        ) : (
          <Badge tone="neutral">نامشخص</Badge>
        )}
      </span>
      {!running && b.state !== 'NONE' && (
        <span className="text-[12px] text-fg-muted">
          شباهت چهره: {pct(b.faceSimilarity)} · زنده‌بودن: {pct(b.livenessScore)} · تطابق گفتار:{' '}
          {pct(b.speechSimilarity)}
        </span>
      )}
      {b.spokenLine && (
        <span className="text-[12px] text-fg-muted">جمله‌ی خواندنی: «{b.spokenLine}»</span>
      )}
      {(b.outcome || b.category) && (
        <TechCode>
          {b.outcome ?? ''} {b.category ?? ''}
        </TechCode>
      )}
      {open && (b.state === 'FAILED' || b.state === 'SKIPPED') && (
        <div className="flex flex-wrap items-center gap-2" data-kyc2-requeue>
          <Button
            size="sm"
            variant="secondary"
            loading={again === 'busy'}
            isDisabled={again === 'done'}
            onPress={() => void requeue()}
          >
            بررسی هوشمند دوباره
          </Button>
          <span className="text-[12px] text-fg-muted">
            {again === 'done'
              ? 'در صف بررسی خودکار قرار گرفت؛ چند دقیقه بعد صفحه را تازه کنید.'
              : again !== 'idle' && again !== 'busy'
                ? again
                : 'همان ویدیو دوباره برای جیبیت ارسال می‌شود؛ کاربر لازم نیست دوباره ضبط کند.'}
          </span>
        </div>
      )}
    </div>
  );
}

function EvidenceViewer({ userId, s }: { userId: string; s: Kyc2Submission }) {
  const [url, setUrl] = useState<string | null>(null);
  const [stepUp, setStepUp] = useState<'VIEW' | 'DOWNLOAD' | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );
  async function fetchEvidence(mode: 'VIEW' | 'DOWNLOAD') {
    setBusy(true);
    setError(null);
    try {
      if (stepUp) {
        await api.post('/auth/step-up', { code });
        setStepUp(null);
        setCode('');
      }
      const blob = await api.blob(`/users/${userId}/kyc2/evidence/${s.evidence.id}`, { mode });
      const u = URL.createObjectURL(blob);
      if (mode === 'VIEW') setUrl(u);
      else {
        const a = document.createElement('a');
        a.href = u;
        a.download = `kyc2-attempt-${String(s.attemptNo)}.${EXT[s.evidence.contentType] ?? 'jpg'}`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(u), 0);
      }
    } catch (e) {
      if (e instanceof ApiFailure && e.code === 'STEP_UP_REQUIRED') setStepUp(mode);
      else setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex flex-col gap-2" data-kyc2-evidence={s.evidence.id}>
      <div className="flex flex-wrap items-center gap-2">
        {url ? (
          <Button size="sm" variant="ghost" onPress={() => setUrl(null)}>
            بستن تصویر
          </Button>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            loading={busy}
            onPress={() => void fetchEvidence('VIEW')}
            data-kyc2-view
          >
            مشاهده‌ی مدرک
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          isDisabled={busy}
          onPress={() => void fetchEvidence('DOWNLOAD')}
          data-kyc2-download
        >
          دریافت فایل اصلی
        </Button>
        <span className="text-[11px] text-fg-subtle">
          هر مشاهده و دریافت در رد ممیزی ثبت می‌شود.
        </span>
      </div>
      {stepUp && (
        <div
          className="flex flex-wrap items-end gap-2 rounded-md border border-accent-soft p-3"
          data-admin-step-up
        >
          <TextField
            label="کد ورود دومرحله‌ای مدیر"
            value={code}
            onChange={setCode}
            ltr
            inputMode="numeric"
          />
          <Button
            size="sm"
            loading={busy}
            isDisabled={code.length !== 6}
            onPress={() => void fetchEvidence(stepUp)}
          >
            تأیید و ادامه
          </Button>
        </div>
      )}
      {error && (
        <p role="alert" className="m-0 text-[13px] text-negative">
          {error}
        </p>
      )}
      {url &&
        (s.evidence.contentType.startsWith('video/') ? (
          <video
            src={url}
            controls
            playsInline
            aria-label={`ویدیوی احراز سطح ۲، تلاش ${String(s.attemptNo)}`}
            className="max-h-[28rem] w-auto max-w-full rounded-md border border-line bg-black"
            data-kyc2-video
          />
        ) : (
          <img
            src={url}
            alt={`مدرک احراز سطح ۲، تلاش ${String(s.attemptNo)}`}
            className="max-h-[28rem] w-auto max-w-full rounded-md border border-line object-contain"
            data-kyc2-image
          />
        ))}
    </div>
  );
}

export function Kyc2Attempts({ userId, items }: { userId: string; items: Kyc2Submission[] }) {
  if (items.length === 0)
    return <StateView compact kind="empty" title="درخواست احراز سطح ۲ ثبت نشده است" />;
  return (
    <ol className="m-0 flex list-none flex-col gap-3 p-0" data-kyc2-attempts>
      {items.map((s) => {
        const st = STATUS[s.status] ?? { label: 'وضعیت ثبت‌شده', tone: 'neutral' as const };
        return (
          <li
            key={s.id}
            className="flex flex-col gap-2 rounded-md border border-line bg-surface-2 p-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[13.5px] font-semibold">
                تلاش {s.attemptNo.toLocaleString('fa-IR')} <Badge tone={st.tone}>{st.label}</Badge>
              </span>
              <span className="text-[12px] text-fg-muted">ارسال: {dt(s.submittedAt)}</span>
            </div>
            <p className="m-0 text-[12.5px] leading-6 text-fg-muted">
              نسخه‌ی قوانین پذیرفته‌شده: <bdi dir="ltr">{s.rulesVersion}</bdi> ·{' '}
              {dt(s.rulesAcceptedAt)}
              {s.decidedAt && <> · تصمیم: {dt(s.decidedAt)}</>}
            </p>
            {s.correctionTargets && s.correctionTargets.length > 0 && (
              <p className="m-0 text-[12.5px]">
                بخش‌های نیازمند اصلاح: {s.correctionTargets.map((t) => TARGET[t] ?? t).join('، ')}
              </p>
            )}
            {s.decisionReason && (
              <p className="m-0 text-[12.5px]">دلیل تصمیم: {s.decisionReason}</p>
            )}
            {s.biometric && (
              <BiometricResult
                b={s.biometric}
                submissionId={s.id}
                open={s.status === 'SUBMITTED'}
              />
            )}
            <TechCode>
              {s.evidence.contentType} · {s.evidence.byteSize} bytes · sha256{' '}
              {s.evidence.sha256.slice(0, 16)}…
            </TechCode>
            <EvidenceViewer userId={userId} s={s} />
          </li>
        );
      })}
    </ol>
  );
}
